// ============================================================================
// Signification Engine — Math Utilities
// ============================================================================
// Compositional data math for tension (1D) and triad (2D) signification.
//
// Barycentric coordinates are compositional data on a simplex (sum = 1, all ≥ 0).
// Standard Euclidean stats are misleading — we use Aitchison geometry (log-ratio
// transforms) for statistically correct operations.
//
// IMPORTANT: A copy of this file exists at functions/src/signification.math.ts
// for server-side use. Keep both copies in sync when modifying.
// ============================================================================

// ---------------------------------------------------------------------------
// 1. Core Coordinate Transforms
// ---------------------------------------------------------------------------

export interface Point2D {
  x: number;
  y: number;
}

export interface BarycentricCoord {
  a: number; // Weight toward vertex A (top / pole 0)
  b: number; // Weight toward vertex B (bottom-left / pole 1)
  c: number; // Weight toward vertex C (bottom-right / pole 2)
}

export interface TriangleVertices {
  A: Point2D; // Top vertex
  B: Point2D; // Bottom-left vertex
  C: Point2D; // Bottom-right vertex
}

/**
 * Compute equilateral triangle vertices for a given container size.
 * Triangle is centered horizontally with configurable padding.
 */
export function computeTriangleVertices(
  width: number,
  height: number,
  padding: number = 40
): TriangleVertices {
  const usableW = width - padding * 2;
  const usableH = height - padding * 2;

  const sideFromWidth = usableW;
  const sideFromHeight = (usableH * 2) / Math.sqrt(3);
  const side = Math.min(sideFromWidth, sideFromHeight);

  const triHeight = (side * Math.sqrt(3)) / 2;
  const cx = width / 2;
  const topY = padding + (usableH - triHeight) / 2;

  return {
    A: { x: cx, y: topY },
    B: { x: cx - side / 2, y: topY + triHeight },
    C: { x: cx + side / 2, y: topY + triHeight },
  };
}

/**
 * Convert screen (x, y) to barycentric (a, b, c).
 * Returns unclamped values — caller decides whether to clamp.
 */
export function screenToBarycentric(
  point: Point2D,
  vertices: TriangleVertices
): BarycentricCoord {
  const { A, B, C } = vertices;
  const denom =
    (B.y - C.y) * (A.x - C.x) + (C.x - B.x) * (A.y - C.y);

  const a =
    ((B.y - C.y) * (point.x - C.x) + (C.x - B.x) * (point.y - C.y)) / denom;
  const b =
    ((C.y - A.y) * (point.x - C.x) + (A.x - C.x) * (point.y - C.y)) / denom;
  const c = 1 - a - b;

  return { a, b, c };
}

/** Convert barycentric to screen coordinates (weighted average of vertices). */
export function barycentricToScreen(
  bary: BarycentricCoord,
  vertices: TriangleVertices
): Point2D {
  const { A, B, C } = vertices;
  return {
    x: bary.a * A.x + bary.b * B.x + bary.c * C.x,
    y: bary.a * A.y + bary.b * B.y + bary.c * C.y,
  };
}

/**
 * Clamp barycentric to simplex (all ≥ 0, sum = 1).
 * Projects to nearest edge/vertex when outside triangle.
 */
export function clampToSimplex(bary: BarycentricCoord): BarycentricCoord {
  let { a, b, c } = bary;
  a = Math.max(0, a);
  b = Math.max(0, b);
  c = Math.max(0, c);

  const sum = a + b + c;
  if (sum === 0) return { a: 1 / 3, b: 1 / 3, c: 1 / 3 };

  return { a: a / sum, b: b / sum, c: c / sum };
}

/** Test whether a screen point is inside the triangle. */
export function isInsideTriangle(
  point: Point2D,
  vertices: TriangleVertices
): boolean {
  const bary = screenToBarycentric(point, vertices);
  return bary.a >= 0 && bary.b >= 0 && bary.c >= 0;
}

// ---------------------------------------------------------------------------
// 2. Tension (1D) Math
// ---------------------------------------------------------------------------

/** Convert screen X to tension weight (0 = left pole, 1 = right pole). */
export function screenToTensionWeight(
  x: number,
  leftX: number,
  rightX: number
): number {
  const raw = (x - leftX) / (rightX - leftX);
  return Math.max(0, Math.min(1, raw));
}

/** Convert tension weight to screen X. */
export function tensionWeightToScreen(
  weight: number,
  leftX: number,
  rightX: number
): number {
  return leftX + weight * (rightX - leftX);
}

/** Tension percentages as integers summing to 100. */
export function tensionPercentages(weight: number): [number, number] {
  const poleB = Math.round(weight * 100);
  return [100 - poleB, poleB];
}

// ---------------------------------------------------------------------------
// 3. Display Formatting
// ---------------------------------------------------------------------------

/**
 * Barycentric to integer percentages summing to exactly 100.
 * Uses largest-remainder method (NOT independent Math.round).
 */
export function baryToPercentages(bary: BarycentricCoord): [number, number, number] {
  const raw = [bary.a * 100, bary.b * 100, bary.c * 100];
  const floored = raw.map(Math.floor);
  let remainder = 100 - floored.reduce((s, v) => s + v, 0);

  const fractions = raw.map((v, i) => ({ i, frac: v - floored[i] }));
  fractions.sort((a, b) => b.frac - a.frac);

  for (let j = 0; j < remainder; j++) {
    floored[fractions[j].i]++;
  }

  return floored as [number, number, number];
}

/**
 * Human-readable placement summary.
 * e.g., "62% Community Action, 28% Self-Agency, 10% State Support"
 */
export function placementSummary(
  bary: BarycentricCoord,
  labels: [string, string, string]
): string {
  const pcts = baryToPercentages(bary);
  const ranked = labels
    .map((label, i) => ({ label, pct: pcts[i] }))
    .sort((a, b) => b.pct - a.pct);

  return ranked.map((r) => `${r.pct}% ${r.label}`).join(', ');
}

// ---------------------------------------------------------------------------
// 4. Compositional Statistics (Aitchison Geometry)
// ---------------------------------------------------------------------------

const EPSILON = 1e-10;

/** Centered log-ratio (CLR) transform: simplex → real-valued vector space. */
export function clrTransform(bary: BarycentricCoord): [number, number, number] {
  const a = Math.max(EPSILON, bary.a);
  const b = Math.max(EPSILON, bary.b);
  const c = Math.max(EPSILON, bary.c);
  const logMean = (Math.log(a) + Math.log(b) + Math.log(c)) / 3;

  return [
    Math.log(a) - logMean,
    Math.log(b) - logMean,
    Math.log(c) - logMean,
  ];
}

/** Inverse CLR: real-valued vector space → simplex. */
export function clrInverse(clr: [number, number, number]): BarycentricCoord {
  const exp = clr.map(Math.exp);
  const sum = exp.reduce((s, v) => s + v, 0);
  return { a: exp[0] / sum, b: exp[1] / sum, c: exp[2] / sum };
}

/** Aitchison distance — geometrically correct distance on the simplex. */
export function aitchisonDistance(
  p: BarycentricCoord,
  q: BarycentricCoord
): number {
  const pClr = clrTransform(p);
  const qClr = clrTransform(q);
  const sumSq = pClr.reduce((s, v, i) => s + (v - qClr[i]) ** 2, 0);
  return Math.sqrt(sumSq);
}

/**
 * Compositional mean (geometric mean on simplex via CLR).
 * The correct way to average positions on a triangle.
 */
export function compositionalMean(points: BarycentricCoord[]): BarycentricCoord {
  if (points.length === 0) return { a: 1 / 3, b: 1 / 3, c: 1 / 3 };

  const clrs = points.map(clrTransform);
  const meanClr: [number, number, number] = [0, 0, 0];

  for (const clr of clrs) {
    meanClr[0] += clr[0] / points.length;
    meanClr[1] += clr[1] / points.length;
    meanClr[2] += clr[2] / points.length;
  }

  return clrInverse(meanClr);
}

/** Compositional variance — total variance in Aitchison geometry. */
export function compositionalVariance(points: BarycentricCoord[]): number {
  if (points.length < 2) return 0;

  const mean = compositionalMean(points);
  const distances = points.map((p) => aitchisonDistance(p, mean));
  const sumSq = distances.reduce((s, d) => s + d * d, 0);

  return sumSq / (points.length - 1);
}

/** Arithmetic mean on simplex — fast, for UI rendering only (not statistical claims). */
export function arithmeticMean(points: BarycentricCoord[]): BarycentricCoord {
  if (points.length === 0) return { a: 1 / 3, b: 1 / 3, c: 1 / 3 };

  const sum = { a: 0, b: 0, c: 0 };
  for (const p of points) {
    sum.a += p.a;
    sum.b += p.b;
    sum.c += p.c;
  }
  const n = points.length;
  return { a: sum.a / n, b: sum.b / n, c: sum.c / n };
}

// ---------------------------------------------------------------------------
// 5. Tension (1D) Statistics
// ---------------------------------------------------------------------------

export function tensionMean(weights: number[]): number {
  if (weights.length === 0) return 0.5;
  return weights.reduce((s, w) => s + w, 0) / weights.length;
}

export function tensionVariance(weights: number[]): number {
  if (weights.length < 2) return 0;
  const mean = tensionMean(weights);
  return weights.reduce((s, w) => s + (w - mean) ** 2, 0) / (weights.length - 1);
}

export function tensionStdDev(weights: number[]): number {
  return Math.sqrt(tensionVariance(weights));
}

/**
 * Bimodality score (0–1). Detects two-peak distributions.
 * Uses variance-ratio split test. Threshold 0.5 for flagging.
 */
export function bimodalityScore(weights: number[]): number {
  if (weights.length < 10) return 0;

  const sorted = [...weights].sort((a, b) => a - b);
  const n = sorted.length;
  let minDip = Infinity;

  for (let splitIdx = Math.floor(n * 0.2); splitIdx <= Math.floor(n * 0.8); splitIdx++) {
    const left = sorted.slice(0, splitIdx);
    const right = sorted.slice(splitIdx);

    if (left.length < 3 || right.length < 3) continue;

    const leftVar = tensionVariance(left);
    const rightVar = tensionVariance(right);
    const totalVar = tensionVariance(weights);

    const withinVar = (leftVar * left.length + rightVar * right.length) / n;
    const dip = withinVar / Math.max(totalVar, EPSILON);
    minDip = Math.min(minDip, dip);
  }

  return Math.max(0, Math.min(1, 1 - minDip));
}

// ---------------------------------------------------------------------------
// 6. Cluster Detection (Grid-Based for Triads)
// ---------------------------------------------------------------------------

export interface Cluster {
  centroid: BarycentricCoord;
  members: number[];        // Indices into input array
  meanDistance: number;      // Mean Aitchison distance from cluster centroid
}

/**
 * Grid-based clustering on the simplex.
 * Divides triangle into grid, counts per cell, merges adjacent dense cells via BFS.
 */
export function gridCluster(
  points: BarycentricCoord[],
  resolution: number = 8,
  minCount: number = 3
): Cluster[] {
  type Cell = { a: number; b: number; c: number; members: number[] };
  const cells: Cell[] = [];

  for (let i = 0; i <= resolution; i++) {
    for (let j = 0; j <= resolution - i; j++) {
      const k = resolution - i - j;
      cells.push({
        a: (i + 0.33) / resolution,
        b: (j + 0.33) / resolution,
        c: (k + 0.33) / resolution,
        members: [],
      });
    }
  }

  // Assign each point to nearest cell
  points.forEach((p, idx) => {
    let minDist = Infinity;
    let bestCell = 0;
    cells.forEach((cell, ci) => {
      const d =
        (p.a - cell.a) ** 2 + (p.b - cell.b) ** 2 + (p.c - cell.c) ** 2;
      if (d < minDist) {
        minDist = d;
        bestCell = ci;
      }
    });
    cells[bestCell].members.push(idx);
  });

  const denseCells = cells.filter((c) => c.members.length >= minCount);

  // Merge adjacent dense cells via BFS
  const merged: Cell[] = [];
  const visited = new Set<number>();
  const threshold = (2.5 / resolution) ** 2;

  denseCells.forEach((cell, i) => {
    if (visited.has(i)) return;
    visited.add(i);

    const group: Cell = { ...cell, members: [...cell.members] };
    const queue = [i];

    while (queue.length > 0) {
      const curr = queue.shift()!;
      denseCells.forEach((other, j) => {
        if (visited.has(j)) return;
        const d =
          (denseCells[curr].a - other.a) ** 2 +
          (denseCells[curr].b - other.b) ** 2 +
          (denseCells[curr].c - other.c) ** 2;
        if (d < threshold) {
          visited.add(j);
          group.members.push(...other.members);
          queue.push(j);
        }
      });
    }

    merged.push(group);
  });

  return merged.map((group) => {
    const memberPoints = group.members.map((i) => points[i]);
    const centroid = arithmeticMean(memberPoints);
    const distances = memberPoints.map((p) => aitchisonDistance(p, centroid));
    const meanDistance =
      distances.reduce((s, d) => s + d, 0) / distances.length;

    return { centroid, members: group.members, meanDistance };
  });
}

// ---------------------------------------------------------------------------
// 7. Weak Signal Detection
// ---------------------------------------------------------------------------

export interface WeakSignal {
  cluster: Cluster;
  divergence: number;
  direction: string;
}

/**
 * Detect clusters whose centroid diverges > threshold σ from population centroid.
 */
export function detectWeakSignals(
  clusters: Cluster[],
  allPoints: BarycentricCoord[],
  labels: [string, string, string],
  threshold: number = 1.5
): WeakSignal[] {
  const popCentroid = compositionalMean(allPoints);
  const popStdDev = Math.sqrt(compositionalVariance(allPoints));

  if (popStdDev < EPSILON) return [];

  return clusters
    .map((cluster) => {
      const divergence = aitchisonDistance(cluster.centroid, popCentroid) / popStdDev;
      const pcts = baryToPercentages(cluster.centroid);
      const maxIdx = pcts.indexOf(Math.max(...pcts));
      const direction = `high ${labels[maxIdx]}`;

      return { cluster, divergence, direction };
    })
    .filter((ws) => ws.divergence >= threshold)
    .sort((a, b) => b.divergence - a.divergence);
}

// ---------------------------------------------------------------------------
// 8. Density Estimation
// ---------------------------------------------------------------------------

/**
 * 2D kernel density on the simplex (Gaussian kernel in CLR space).
 * Returns normalized density grid for heatmap rendering.
 */
export function simplexDensity(
  points: BarycentricCoord[],
  gridSize: number = 30,
  bandwidth?: number
): Array<{ bary: BarycentricCoord; density: number }> {
  if (points.length === 0) return [];

  const h =
    bandwidth ??
    Math.sqrt(compositionalVariance(points)) *
      Math.pow(points.length, -1 / 5) *
      1.2;

  const hSq = h * h;
  const result: Array<{ bary: BarycentricCoord; density: number }> = [];

  for (let i = 1; i < gridSize; i++) {
    for (let j = 1; j < gridSize - i; j++) {
      const k = gridSize - i - j;
      const bary: BarycentricCoord = {
        a: i / gridSize,
        b: j / gridSize,
        c: k / gridSize,
      };

      let density = 0;
      for (const p of points) {
        const d = aitchisonDistance(p, bary);
        density += Math.exp(-(d * d) / (2 * hSq));
      }
      density /= points.length * h * Math.sqrt(2 * Math.PI);

      result.push({ bary, density });
    }
  }

  const maxDensity = Math.max(...result.map((r) => r.density));
  if (maxDensity > 0) {
    for (const r of result) {
      r.density /= maxDensity;
    }
  }

  return result;
}

/**
 * 1D kernel density for tension weights.
 * Returns density curve points for rendering.
 */
export function tensionDensity(
  weights: number[],
  nPoints: number = 100,
  bandwidth?: number
): Array<{ x: number; density: number }> {
  if (weights.length === 0) return [];

  const h = bandwidth ?? tensionStdDev(weights) * Math.pow(weights.length, -1 / 5) * 1.06;
  const hSq = h * h;
  const result: Array<{ x: number; density: number }> = [];

  for (let i = 0; i <= nPoints; i++) {
    const x = i / nPoints;
    let density = 0;
    for (const w of weights) {
      const d = x - w;
      density += Math.exp(-(d * d) / (2 * hSq));
    }
    density /= weights.length * h * Math.sqrt(2 * Math.PI);
    result.push({ x, density });
  }

  const maxDensity = Math.max(...result.map((r) => r.density));
  if (maxDensity > 0) {
    for (const r of result) {
      r.density /= maxDensity;
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// 9. Selection / Lasso Geometry
// ---------------------------------------------------------------------------

/** Test whether a tension weight falls within a selection range. */
export function isInTensionRange(
  weight: number,
  min: number,
  max: number
): boolean {
  return weight >= min && weight <= max;
}

/**
 * Test whether a barycentric point is inside a polygon.
 * Ray-casting algorithm in screen coordinates.
 */
export function isInSimplexPolygon(
  point: BarycentricCoord,
  polygon: BarycentricCoord[],
  vertices: TriangleVertices
): boolean {
  const p = barycentricToScreen(point, vertices);
  const polyScreen = polygon.map((v) => barycentricToScreen(v, vertices));

  let inside = false;
  for (let i = 0, j = polyScreen.length - 1; i < polyScreen.length; j = i++) {
    const xi = polyScreen[i].x, yi = polyScreen[i].y;
    const xj = polyScreen[j].x, yj = polyScreen[j].y;

    const intersect =
      yi > p.y !== yj > p.y &&
      p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
}

// ---------------------------------------------------------------------------
// 10. Color Interpolation
// ---------------------------------------------------------------------------

/** Blend three vertex colors by barycentric weights → RGB string. */
export function barycentricColorBlend(
  bary: BarycentricCoord,
  colorA: [number, number, number],
  colorB: [number, number, number],
  colorC: [number, number, number]
): string {
  const r = Math.round(bary.a * colorA[0] + bary.b * colorB[0] + bary.c * colorC[0]);
  const g = Math.round(bary.a * colorA[1] + bary.b * colorB[1] + bary.c * colorC[1]);
  const b = Math.round(bary.a * colorA[2] + bary.b * colorB[2] + bary.c * colorC[2]);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Blend two pole colors by tension weight → RGB string. */
export function tensionColorBlend(
  weight: number,
  colorA: [number, number, number],
  colorB: [number, number, number]
): string {
  const r = Math.round((1 - weight) * colorA[0] + weight * colorB[0]);
  const g = Math.round((1 - weight) * colorA[1] + weight * colorB[1]);
  const b = Math.round((1 - weight) * colorA[2] + weight * colorB[2]);
  return `rgb(${r}, ${g}, ${b})`;
}

// ---------------------------------------------------------------------------
// 11. Stones Math — proximity, clustering, aggregation
// ---------------------------------------------------------------------------

export interface StonePlacementData {
  stoneId: string;
  x: number;  // 0–1 normalized
  y: number;
}

/** Euclidean distance between two points in normalized [0,1] space. */
export function stonesDistance(
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/**
 * Compute average position of each stone across all participants.
 * Input: array of participant placement arrays.
 * Returns: map of stoneId → { x, y, varianceX, varianceY }.
 */
export function stonesAveragePositions(
  allPlacements: StonePlacementData[][]
): Record<string, { x: number; y: number; varianceX: number; varianceY: number }> {
  const accum: Record<string, { xs: number[]; ys: number[] }> = {};

  for (const placements of allPlacements) {
    for (const p of placements) {
      if (!accum[p.stoneId]) accum[p.stoneId] = { xs: [], ys: [] };
      accum[p.stoneId].xs.push(p.x);
      accum[p.stoneId].ys.push(p.y);
    }
  }

  const result: Record<string, { x: number; y: number; varianceX: number; varianceY: number }> = {};
  for (const [id, { xs, ys }] of Object.entries(accum)) {
    const n = xs.length;
    const mx = xs.reduce((s, v) => s + v, 0) / n;
    const my = ys.reduce((s, v) => s + v, 0) / n;
    const vx = n > 1 ? xs.reduce((s, v) => s + (v - mx) ** 2, 0) / (n - 1) : 0;
    const vy = n > 1 ? ys.reduce((s, v) => s + (v - my) ** 2, 0) / (n - 1) : 0;
    result[id] = { x: mx, y: my, varianceX: vx, varianceY: vy };
  }
  return result;
}

/**
 * Compute average inter-stone distances across all participants.
 * Returns a symmetric matrix: proximityMatrix[stoneA][stoneB] = average distance.
 */
export function stonesProximityMatrix(
  allPlacements: StonePlacementData[][]
): Record<string, Record<string, number>> {
  const pairAccum: Record<string, number[]> = {};

  for (const placements of allPlacements) {
    const byId: Record<string, { x: number; y: number }> = {};
    for (const p of placements) byId[p.stoneId] = { x: p.x, y: p.y };

    const ids = Object.keys(byId).sort();
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const key = `${ids[i]}::${ids[j]}`;
        if (!pairAccum[key]) pairAccum[key] = [];
        pairAccum[key].push(stonesDistance(byId[ids[i]], byId[ids[j]]));
      }
    }
  }

  const matrix: Record<string, Record<string, number>> = {};
  for (const [key, distances] of Object.entries(pairAccum)) {
    const [a, b] = key.split('::');
    const avg = distances.reduce((s, v) => s + v, 0) / distances.length;
    if (!matrix[a]) matrix[a] = {};
    if (!matrix[b]) matrix[b] = {};
    matrix[a][b] = avg;
    matrix[b][a] = avg;
  }
  return matrix;
}

/**
 * Find groups of stones that are consistently placed near each other.
 * Uses single-linkage clustering: if avg distance < threshold, merge.
 * Default threshold: 0.25 in normalized [0,1] space (25% of canvas).
 */
export function stonesConsistentClusters(
  proximityMatrix: Record<string, Record<string, number>>,
  threshold: number = 0.25
): string[][] {
  const ids = Object.keys(proximityMatrix);
  const parent: Record<string, string> = {};
  for (const id of ids) parent[id] = id;

  function find(x: string): string {
    while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; }
    return x;
  }
  function union(a: string, b: string) {
    parent[find(a)] = find(b);
  }

  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      const dist = proximityMatrix[ids[i]]?.[ids[j]];
      if (dist !== undefined && dist < threshold) {
        union(ids[i], ids[j]);
      }
    }
  }

  const groups: Record<string, string[]> = {};
  for (const id of ids) {
    const root = find(id);
    if (!groups[root]) groups[root] = [];
    groups[root].push(id);
  }

  // Only return groups with 2+ stones
  return Object.values(groups).filter(g => g.length >= 2);
}

/**
 * Find stones that are consistently placed far from all others.
 * A stone is an isolate if all its average distances exceed the threshold.
 */
export function stonesConsistentIsolates(
  proximityMatrix: Record<string, Record<string, number>>,
  threshold: number = 0.45
): string[] {
  const isolates: string[] = [];
  for (const [id, distances] of Object.entries(proximityMatrix)) {
    const vals = Object.values(distances);
    if (vals.length > 0 && vals.every(d => d >= threshold)) {
      isolates.push(id);
    }
  }
  return isolates;
}

/**
 * Generate a human-readable summary of stone placements.
 * E.g., "You placed Bonding and Mood closest together, with Identity separate."
 */
export function stonesSummary(
  placements: StonePlacementData[],
  labels: Record<string, string>
): string {
  if (placements.length < 2) return '';

  // Find the closest pair
  let minDist = Infinity;
  let closestA = '';
  let closestB = '';
  for (let i = 0; i < placements.length; i++) {
    for (let j = i + 1; j < placements.length; j++) {
      const d = stonesDistance(placements[i], placements[j]);
      if (d < minDist) {
        minDist = d;
        closestA = placements[i].stoneId;
        closestB = placements[j].stoneId;
      }
    }
  }

  // Find the most isolated stone (highest average distance to others)
  let maxAvgDist = -1;
  let isolated = '';
  for (const p of placements) {
    const dists = placements
      .filter(q => q.stoneId !== p.stoneId)
      .map(q => stonesDistance(p, q));
    const avg = dists.reduce((s, v) => s + v, 0) / dists.length;
    if (avg > maxAvgDist) {
      maxAvgDist = avg;
      isolated = p.stoneId;
    }
  }

  const labelA = labels[closestA] || closestA;
  const labelB = labels[closestB] || closestB;
  const labelIso = labels[isolated] || isolated;

  if (isolated === closestA || isolated === closestB) {
    return `You placed ${labelA} and ${labelB} closest together.`;
  }
  return `You placed ${labelA} and ${labelB} closest together, with ${labelIso} separate.`;
}
